import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useRef, useImperativeHandle, useEffect } from "react";
import { useTheme, Radio, Stack, Zoom } from "@mui/material";
import warning from "tiny-warning";
import { useThemeColor } from "../../../themes/hooks/useThemeColor.es.js";
const SIZES = {
  small: { button: 16, dot: 6 },
  medium: { button: 20, dot: 7 },
  large: { button: 24, dot: 8 }
};
const VARIANT_COLORS = (themeColor, grayColor, whiteColor, disabledBackgroundColor, disabledBorderColor, disabledColor) => ({
  outlined: {
    unchecked: { background: whiteColor, border: grayColor },
    uncheckedDisabled: {
      background: disabledBackgroundColor,
      border: disabledBorderColor
    },
    checked: { background: whiteColor, border: themeColor, dot: themeColor },
    checkedDisabled: {
      background: disabledBackgroundColor,
      border: disabledBorderColor,
      dot: disabledColor
    },
    hover: { background: themeColor, border: themeColor, dot: whiteColor }
  },
  filled: {
    unchecked: { background: whiteColor, border: whiteColor },
    uncheckedDisabled: {
      background: disabledBackgroundColor,
      border: disabledBorderColor
    },
    checked: { background: whiteColor, border: whiteColor, dot: themeColor },
    checkedDisabled: {
      background: disabledBackgroundColor,
      border: disabledBorderColor,
      dot: disabledColor
    },
    hover: { background: themeColor, border: themeColor, dot: whiteColor }
  },
  "filled-inverse": {
    unchecked: { background: whiteColor, border: grayColor },
    uncheckedDisabled: {
      background: disabledBorderColor,
      border: disabledColor
    },
    checked: { background: themeColor, border: themeColor, dot: whiteColor },
    checkedDisabled: {
      background: disabledColor,
      border: disabledColor,
      dot: disabledBackgroundColor
    },
    hover: { background: themeColor, border: themeColor, dot: whiteColor }
  }
});
const iconProps = (bgcolor, borderColor, diameter) => ({
  alignItems: "center",
  bgcolor,
  border: `1px solid ${borderColor}`,
  borderRadius: "50%",
  height: diameter,
  justifyContent: "space-around",
  width: diameter
});
const iconDotProps = (bgcolor, diameter) => ({
  bgcolor,
  borderRadius: "50%",
  content: '""',
  height: diameter,
  width: diameter
});
const EzRadioIcon = ({ bgcolor, borderColor, diameter, dotColor, dotDiameter }) => /* @__PURE__ */ jsx(
  Stack,
  {
    ...iconProps(bgcolor, borderColor, diameter),
    className: "EzRadioIcon-unchecked",
    sx: { "&:before": { ...iconDotProps(dotColor, dotDiameter) } }
  }
);
const EzRadioCheckedIcon = ({ bgcolor, borderColor, diameter, dotColor, dotDiameter }) => /* @__PURE__ */ jsxs(Stack, { position: "relative", children: [
  /* @__PURE__ */ jsx(
    Stack,
    {
      ...iconProps(bgcolor, borderColor, diameter),
      borderColor,
      className: "EzRadioIcon-checked"
    }
  ),
  /* @__PURE__ */ jsx(Zoom, { in: true, children: /* @__PURE__ */ jsx(
    Stack,
    {
      ...iconDotProps(dotColor, dotDiameter),
      className: "EzRadioIcon-checked-dot",
      position: "absolute",
      right: diameter / 2 - dotDiameter / 2,
      top: diameter / 2 - dotDiameter / 2
    }
  ) })
] });
const EzRadioMui = forwardRef(
  ({ ariaLabel, color, disabled, size, variant, ...props }, ref) => {
    const theme = useTheme();
    const themeColor = useThemeColor(color);
    const getDiameter = (part) => SIZES[size][part];
    const getColor = (checked, hover, style) => {
      const radioState = `${checked ? "checked" : "unchecked"}${disabled ? "Disabled" : ""}`;
      return VARIANT_COLORS(
        themeColor,
        theme.palette.common.grey140,
        theme.palette.common.white,
        theme.palette.common.grey120,
        // disabled background color
        theme.palette.common.grey130,
        // disabled border color
        theme.palette.common.disabled
        // disabled color
      )[variant][hover ? "hover" : radioState][style];
    };
    const radioIconProps = (checked) => ({
      bgcolor: getColor(checked, false, "background"),
      borderColor: getColor(checked, false, "border"),
      diameter: getDiameter("button"),
      dotColor: getColor(checked, false, "dot"),
      dotDiameter: getDiameter("dot")
    });
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => inputRef.current);
    useEffect(() => {
      var _a;
      if (process.env.NODE_ENV !== "production")
        warning(
          Boolean(ariaLabel || ((_a = inputRef.current) == null ? void 0 : _a.closest("label"))),
          "<EzRadio /> was used without an `ariaLabel` attribute and without being wrapped in a label"
        );
    }, [ariaLabel, inputRef]);
    return /* @__PURE__ */ jsx(
      Radio,
      {
        inputRef,
        checkedIcon: /* @__PURE__ */ jsx(EzRadioCheckedIcon, { ...radioIconProps(true) }),
        classes: {
          root: `EzRadio EzRadio-${variant}`,
          checked: "EzRadio-checked",
          disabled: "EzRadio-disabled"
        },
        color: (color == null ? void 0 : color.startsWith("common.")) ? void 0 : color,
        disableRipple: true,
        disabled,
        inputProps: { "aria-label": ariaLabel, className: "EzRadio-input" },
        icon: /* @__PURE__ */ jsx(EzRadioIcon, { ...radioIconProps(false) }),
        sx: {
          p: "7px",
          "&:hover": {
            bgcolor: "transparent",
            ".EzRadioIcon-unchecked": {
              bgcolor: getColor(false, true, "background"),
              borderColor: getColor(false, true, "border"),
              "&:before": { bgcolor: getColor(false, true, "dot") }
            }
          },
          "&:focus-within .EzRadioIcon-checked": {
            outline: `2px solid ${theme.palette.common.black}`,
            outlineOffset: "2px"
          }
        },
        ...props
      }
    );
  }
);
EzRadioMui.displayName = "EzRadioMui";
export {
  EzRadioMui as default
};
//# sourceMappingURL=EzRadioMui.es.js.map
